import { ColumnGrouping } from '../models/menu/section/column-grouping';
import { ColWidth } from '../models/shared/col-width';
import { ColumnWidth } from '../models/enum/shared/column-width.enum';
import type { ProductMenu } from '../models/menu/product-menu';
import { SectionColumnConfigKey } from '../models/enum/shared/section-column-config-key';
import { SectionColumnConfig } from '../models/menu/section/section-column-config';
import { SectionColumnConfigProductInfoKey } from '../models/enum/shared/section-column-config-product-info-key';
import { SectionColumnConfigCannabinoidKey } from '../models/enum/shared/section-column-config-cannabinoid-key';
import { SectionColumnConfigTerpeneKey } from '../models/enum/shared/section-column-config-terpene-key';
import { SectionColumnProductInfoType } from '../models/enum/shared/section-column-product-info-type';
import { SectionColumnCannabinoidType } from '../models/enum/shared/section-column-cannabinoid-type';
import { SectionColumnTerpeneType } from '../models/enum/shared/section-column-terpene-type';
import { isProductTagGroupKey } from '../models/enum/shared/section-theme-tag-group-keys';

// @dynamic
export class ColumnUtils {
  /* ***************************** Column Widths ***************************** */

  static getProducerColWidth(stdPercentage: number): ColWidth {
    return new ColWidth(SectionColumnProductInfoType.Producer, stdPercentage);
  }

  static getBadgeColWidth(
    stdPercentage: number,
    sectionColumnConfigMap: Map<SectionColumnConfigKey, SectionColumnConfig | null>
  ): ColWidth {
    const badgeSectionWidth = sectionColumnConfigMap?.get(SectionColumnConfigProductInfoKey.Badges)?.columnWidth;
    const badgeWidth = badgeSectionWidth || stdPercentage;
    return new ColWidth(SectionColumnProductInfoType.Badge, badgeWidth);
  }

  static getBrandColWidth(sectionColumnConfigMap: Map<SectionColumnConfigKey, SectionColumnConfig | null>): ColWidth {
    const brandSectionWidth = sectionColumnConfigMap?.get(SectionColumnConfigProductInfoKey.Brand)?.columnWidth;
    const brandWidth = brandSectionWidth || ColumnWidth.BrandDefault;
    return new ColWidth(SectionColumnProductInfoType.Brand, brandWidth);
  }

  static getStrainClassColWidth(
    stdPercentage: number,
    sectionColumnConfigMap: Map<SectionColumnConfigKey, SectionColumnConfig | null>
  ): ColWidth {
    const sClassSectionWidth = sectionColumnConfigMap?.get(SectionColumnConfigProductInfoKey.StrainType)?.columnWidth;
    const sClassWidth = sClassSectionWidth || stdPercentage;
    const strainClass = SectionColumnProductInfoType.StrainType;
    return new ColWidth(strainClass, sClassWidth);
  }

  static getStrainColWidth(
    stdPercentage: number,
    sectionColumnConfigMap: Map<SectionColumnConfigKey, SectionColumnConfig | null>
  ): ColWidth {
    const strainSectionWidth = sectionColumnConfigMap?.get(SectionColumnConfigProductInfoKey.Strain)?.columnWidth;
    const strainWidth = strainSectionWidth || stdPercentage;
    return new ColWidth(SectionColumnProductInfoType.Strain, strainWidth);
  }

  static getLineageColWidth(
    stdPercentage: number,
    sectionColumnConfigMap: Map<SectionColumnConfigKey, SectionColumnConfig | null>
  ): ColWidth {
    const lineageSectionWidth = sectionColumnConfigMap?.get(SectionColumnConfigProductInfoKey.Lineage)?.columnWidth;
    const lineageWidth = lineageSectionWidth || stdPercentage;
    return new ColWidth(SectionColumnProductInfoType.Lineage, lineageWidth);
  }

  static getAssetColWidth(
    stdPercentage: number,
    sectionColumnConfigMap: Map<SectionColumnConfigKey, SectionColumnConfig | null>
  ): ColWidth {
    const assetSectionWidth = sectionColumnConfigMap?.get(SectionColumnConfigProductInfoKey.Asset)?.columnWidth;
    const assetWidth = assetSectionWidth || stdPercentage;
    return new ColWidth(SectionColumnProductInfoType.Asset, assetWidth || stdPercentage);
  }

  static getQuantityInStockColWidth(
    stdPercentage: number,
    sectionColumnConfigMap: Map<SectionColumnConfigKey, SectionColumnConfig | null>
  ): ColWidth {
    // Quantity In Stock
    const qtyInStockSectionWidth = sectionColumnConfigMap?.get(SectionColumnConfigProductInfoKey.Stock)?.columnWidth;
    const qtyInStockWidth = qtyInStockSectionWidth || stdPercentage;
    return new ColWidth(SectionColumnProductInfoType.Stock, qtyInStockWidth);
  }

  static getHelpsWithColWidth(
    stdPercentage: number,
    sectionColumnConfigMap: Map<SectionColumnConfigKey, SectionColumnConfig | null>
  ) {
    const typeSectionWidth = sectionColumnConfigMap?.get(SectionColumnConfigProductInfoKey.HelpsWith)?.columnWidth;
    return new ColWidth(SectionColumnConfigProductInfoKey.HelpsWith, typeSectionWidth || stdPercentage);
  }

  static getEffectsColWidth(
    stdPercentage: number,
    sectionColumnConfigMap: Map<SectionColumnConfigKey, SectionColumnConfig | null>
  ) {
    const typeSectionWidth = sectionColumnConfigMap?.get(SectionColumnConfigProductInfoKey.Effects)?.columnWidth;
    return new ColWidth(SectionColumnConfigProductInfoKey.Effects, typeSectionWidth || stdPercentage);
  }

  static getAromaColWidth(
    stdPercentage: number,
    sectionColumnConfigMap: Map<SectionColumnConfigKey, SectionColumnConfig | null>
  ) {
    const typeSectionWidth = sectionColumnConfigMap?.get(SectionColumnConfigProductInfoKey.Aroma)?.columnWidth;
    return new ColWidth(SectionColumnConfigProductInfoKey.Aroma, typeSectionWidth || stdPercentage);
  }

  static getAppearanceColWidth(
    stdPercentage: number,
    sectionColumnConfigMap: Map<SectionColumnConfigKey, SectionColumnConfig | null>
  ) {
    const typeSectionWidth = sectionColumnConfigMap?.get(SectionColumnConfigProductInfoKey.Appearance)?.columnWidth;
    return new ColWidth(SectionColumnConfigProductInfoKey.Appearance, typeSectionWidth || stdPercentage);
  }

  static getGrowingConditionsColWidth(
    stdPercentage: number,
    sectionColumnConfigMap: Map<SectionColumnConfigKey, SectionColumnConfig | null>
  ) {
    const typeSectionWidth = sectionColumnConfigMap?.get(
      SectionColumnConfigProductInfoKey.GrowingConditions
    )?.columnWidth;
    return new ColWidth(SectionColumnConfigProductInfoKey.GrowingConditions, typeSectionWidth || stdPercentage);
  }

  static getCraftDesignationColWidth(
    stdPercentage: number,
    sectionColumnConfigMap: Map<SectionColumnConfigKey, SectionColumnConfig | null>
  ) {
    const typeSectionWidth = sectionColumnConfigMap?.get(
      SectionColumnConfigProductInfoKey.CraftDesignation
    )?.columnWidth;
    return new ColWidth(SectionColumnConfigProductInfoKey.CraftDesignation, typeSectionWidth || stdPercentage);
  }

  static getFlavorColWidth(
    stdPercentage: number,
    sectionColumnConfigMap: Map<SectionColumnConfigKey, SectionColumnConfig | null>
  ) {
    const typeSectionWidth = sectionColumnConfigMap?.get(SectionColumnConfigProductInfoKey.Flavor)?.columnWidth;
    return new ColWidth(SectionColumnConfigProductInfoKey.Flavor, typeSectionWidth || stdPercentage);
  }

  static getTopFlavorColWidth(
    stdPercentage: number,
    sectionColumnConfigMap: Map<SectionColumnConfigKey, SectionColumnConfig | null>
  ) {
    const typeSectionWidth = sectionColumnConfigMap?.get(SectionColumnConfigProductInfoKey.TopFlavor)?.columnWidth;
    return new ColWidth(SectionColumnConfigProductInfoKey.TopFlavor, typeSectionWidth || stdPercentage);
  }

  static getEnergyScoreColWidth(
    stdPercentage: number,
    sectionColumnConfigMap: Map<SectionColumnConfigKey, SectionColumnConfig | null>
  ) {
    const typeSectionWidth = sectionColumnConfigMap?.get(SectionColumnConfigProductInfoKey.EnergyScore)?.columnWidth;
    return new ColWidth(SectionColumnConfigProductInfoKey.EnergyScore, typeSectionWidth || stdPercentage);
  }

  static getVariantTypeColWidth(
    stdPercentage: number,
    sectionColumnConfigMap: Map<SectionColumnConfigKey, SectionColumnConfig | null>
  ): ColWidth {
    const variantTypeSectionWidth = sectionColumnConfigMap?.get(
      SectionColumnConfigProductInfoKey.VariantType
    )?.columnWidth;
    const variantTypeWidth = variantTypeSectionWidth || stdPercentage;
    return new ColWidth(SectionColumnConfigProductInfoKey.VariantType, variantTypeSectionWidth);
  }

  static getQuantityAndSizeColWidth(
    stdPercentage: number,
    sectionColumnConfigMap: Map<SectionColumnConfigKey, SectionColumnConfig | null>
  ): ColWidth {
    const qtyAndSizeSectionWidth = sectionColumnConfigMap?.get(
      SectionColumnConfigProductInfoKey.QuantityAndSize
    )?.columnWidth;
    const qtyAndSizeWidth = qtyAndSizeSectionWidth || stdPercentage;
    return new ColWidth(SectionColumnProductInfoType.QuantityAndSize, qtyAndSizeWidth);
  }

  static getQuantityColWidth(
    stdPercentage: number,
    sectionColumnConfigMap: Map<SectionColumnConfigKey, SectionColumnConfig | null>
  ): ColWidth {
    const quantitySectionWidth = sectionColumnConfigMap?.get(SectionColumnConfigProductInfoKey.Quantity)?.columnWidth;
    const quantityWidth = quantitySectionWidth || stdPercentage;
    return new ColWidth(SectionColumnProductInfoType.Quantity, quantityWidth || stdPercentage);
  }

  static getSizeColWidth(
    stdPercentage: number,
    sectionColumnConfigMap: Map<SectionColumnConfigKey, SectionColumnConfig>
  ): ColWidth {
    const sizeSectionWidth = sectionColumnConfigMap?.get(SectionColumnConfigProductInfoKey.Size)?.columnWidth;
    const sizeWidth = sizeSectionWidth || stdPercentage;
    return new ColWidth(SectionColumnProductInfoType.Size, sizeWidth);
  }

  static getGradingResultsColWidth(
    stdPercentage: number,
    sectionColumnConfigMap: Map<SectionColumnConfigKey, SectionColumnConfig | null>
  ): ColWidth {
    const gradingResultsSectionWidth = sectionColumnConfigMap?.get(
      SectionColumnConfigProductInfoKey.GradingResults
    )?.columnWidth;
    const gradingResultsWidth = gradingResultsSectionWidth || stdPercentage;
    return new ColWidth(SectionColumnProductInfoType.GradingResults, gradingResultsWidth);
  }

  static getPriceColWidth(
    stdPercentage: number,
    sectionColumnConfigMap: Map<SectionColumnConfigKey, SectionColumnConfig | null>
  ): ColWidth {
    const priceSectionWidth = sectionColumnConfigMap?.get(SectionColumnConfigProductInfoKey.Price)?.columnWidth;
    const priceWidth = priceSectionWidth || stdPercentage;
    return new ColWidth(SectionColumnProductInfoType.VariantPrice, priceWidth);
  }

  static getSecondaryPriceColWidth(
    stdPercentage: number,
    sectionColumnConfigMap: Map<SectionColumnConfigKey, SectionColumnConfig | null>
  ): ColWidth {
    const secondaryPriceSectionWidth = sectionColumnConfigMap?.get(
      SectionColumnConfigProductInfoKey.SecondaryPrice
    )?.columnWidth;
    const sPriceWidth = secondaryPriceSectionWidth || stdPercentage;
    const sPrice = SectionColumnProductInfoType.VariantSecondaryPrice;
    return new ColWidth(sPrice, sPriceWidth);
  }

  static getSpacerColWidth(productMenu: ProductMenu): ColWidth {
    return new ColWidth(SectionColumnProductInfoType.Spacer, productMenu?.getColumnGroupingSpacerWidthPercentage());
  }

  static getTinySpacerColWidth(): ColWidth {
    return new ColWidth(SectionColumnProductInfoType.TinySpacer, ColumnWidth.TinySpacerDefault);
  }

  static getPrimaryCannabinoidColWidths(
    sectionColumnConfigMap: Map<SectionColumnConfigKey, SectionColumnConfig | null>
  ): ColWidth[] {
    const thcSectionWidth = sectionColumnConfigMap?.get(SectionColumnConfigCannabinoidKey.THC)?.columnWidth;
    const cbdSectionWidth = sectionColumnConfigMap?.get(SectionColumnConfigCannabinoidKey.CBD)?.columnWidth;
    const tacSectionWidth = sectionColumnConfigMap?.get(SectionColumnConfigCannabinoidKey.TAC)?.columnWidth;
    const thcWidth = thcSectionWidth || ColumnWidth.CannabinoidDefault;
    const cbdWidth = cbdSectionWidth || ColumnWidth.CannabinoidDefault;
    const tacWidth = tacSectionWidth || ColumnWidth.CannabinoidDefault;
    return [
      new ColWidth(SectionColumnCannabinoidType.THC, thcWidth),
      new ColWidth(SectionColumnCannabinoidType.CBD, cbdWidth),
      new ColWidth(SectionColumnCannabinoidType.TAC, tacWidth),
      // THC and CBD - not returned from API. Used in some custom themes
      new ColWidth(SectionColumnCannabinoidType.THCAndCBD, ColumnWidth.ThcAndCbdDefault)
    ];
  }

  static getSecondaryCannabinoidColWidths(
    sectionColumnConfigMap: Map<SectionColumnConfigKey, SectionColumnConfig | null>
  ): ColWidth[] {
    return Object.values(SectionColumnConfigCannabinoidKey).map(cannabinoidKey => {
      const sectionDefinedWidth = sectionColumnConfigMap?.get(cannabinoidKey)?.columnWidth;
      const width = sectionDefinedWidth || ColumnWidth.CannabinoidDefault;
      const cannabinoidType = SectionColumnCannabinoidType[cannabinoidKey];
      return new ColWidth(cannabinoidType, width);
    });
  }

  static getProductTagGroupColWidths(
    sectionColumnConfigMap: Map<SectionColumnConfigKey, SectionColumnConfig | null>
  ): ColWidth[] {
    const keys = Array.from(sectionColumnConfigMap.keys());
    const tagGroupKeys = keys.filter(key => isProductTagGroupKey(key));
    return tagGroupKeys.map(tagGroupKey => {
      const sectionDefinedWidth = sectionColumnConfigMap?.get(tagGroupKey)?.columnWidth;
      const width = sectionDefinedWidth || ColumnWidth.ProductTagGroupDefault;
      return new ColWidth(tagGroupKey, width);
    });
  }

  static getTerpeneColWidths(
    sectionColumnConfigMap: Map<SectionColumnConfigKey, SectionColumnConfig | null>
  ): ColWidth[] {
    return Object.values(SectionColumnConfigTerpeneKey).map(terpeneKey => {
      const sectionDefinedWidth = sectionColumnConfigMap?.get(terpeneKey)?.columnWidth;
      const width = sectionDefinedWidth || ColumnWidth.TerpeneDefault;
      const terpeneType = SectionColumnTerpeneType[terpeneKey];
      return new ColWidth(terpeneType, width);
    });
  }

  /* ***************************** Column Groupings ***************************** */

  /**
   * Product Tag Group columns won't work with this, since they are dynamic and not known ahead of time.
   * A refactor will be needed to support them at some point.
   */
  static oneBigGrouping(): ColumnGrouping[] {
    return [
      new ColumnGrouping([
        SectionColumnProductInfoType.StrainType,
        SectionColumnProductInfoType.Strain,
        SectionColumnProductInfoType.Brand,
        SectionColumnProductInfoType.Lineage,
        SectionColumnProductInfoType.Stock,
        SectionColumnProductInfoType.Quantity,
        SectionColumnProductInfoType.Size,
        SectionColumnProductInfoType.GradingResults,
        SectionColumnProductInfoType.VariantPrice,
        SectionColumnProductInfoType.VariantSecondaryPrice,
        ...Object.values(SectionColumnCannabinoidType),
        ...Object.values(SectionColumnTerpeneType)
      ])
    ];
  }

  /**
   * Product Tag Group columns won't work with this, since they are dynamic and not known ahead of time.
   * A refactor will be needed to support them at some point.
   */
  static standardizedGrouping(): ColumnGrouping[] {
    return [
      new ColumnGrouping([
        SectionColumnProductInfoType.StrainType,
        SectionColumnProductInfoType.Strain,
        SectionColumnProductInfoType.Brand,
        SectionColumnProductInfoType.Lineage
      ]),
      new ColumnGrouping(Object.values(SectionColumnCannabinoidType)),
      new ColumnGrouping(Object.values(SectionColumnTerpeneType)),
      new ColumnGrouping([
        SectionColumnProductInfoType.Stock,
        SectionColumnProductInfoType.QuantityAndSize,
        SectionColumnProductInfoType.Quantity,
        SectionColumnProductInfoType.Size,
        SectionColumnProductInfoType.GradingResults,
        SectionColumnProductInfoType.VariantPrice,
        SectionColumnProductInfoType.VariantSecondaryPrice
      ])
    ];
  }
}
